// The list's TABS row (`list_view.tabs`): one tab per condition the designer
// defined — or per value of one picklist field — with each tab's record count,
// and whatever the host puts at the end of the row (the list layout switch).
//
// The tabs are DATA: the screen draws them from the model at once (listTabsOf)
// and from the server's echo once it answers (which adds the counts). Picking
// one only NAMES it to the server, which reads its conditions from the saved
// model. `chips` draws the same tabs as a scrolling chip row (the phone).

import type { ReactNode } from 'react'
import { tw } from '../../testHooks'
import type { TabEcho } from './listTabs'

export default function ListTabs({
  tabs, active, onSelect, showCounts = true, trailing, busy = false, variant = 'tabs',
}: {
  tabs: TabEcho[]
  active: string | null
  onSelect: (id: string) => void
  showCounts?: boolean
  /** Drawn at the end of the row (the list layout switch). */
  trailing?: ReactNode
  busy?: boolean
  variant?: 'tabs' | 'chips'
}) {
  if (variant === 'chips') {
    return (
      <div role="tablist" aria-label="Tabs"
           style={{ display: 'flex', gap: 8, overflowX: 'auto', padding: '0 0 2px' }}>
        {tabs.map((t) => {
          const on = t.id === active
          return (
            <button key={t.id} type="button" role="tab" aria-selected={on}
                    disabled={busy && !on} onClick={() => { if (!on) onSelect(t.id) }}
                    {...tw('action', `list-tab:${t.id}`)}
                    style={{
                      flex: 'none', height: 36, padding: '0 14px', borderRadius: 999,
                      border: `1px solid ${on ? 'var(--color-text, #1c2430)' : 'var(--color-border-strong, #c7cfd9)'}`,
                      background: on ? 'var(--color-text, #1c2430)' : 'var(--color-bg-secondary, #ffffff)',
                      color: on ? 'var(--color-bg-secondary, #ffffff)' : 'var(--color-text-secondary, #465062)',
                      fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer',
                      display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'inherit',
                    }}>
              {t.label}{showCounts && t.count != null && <span>{t.count}</span>}
            </button>
          )
        })}
      </div>
    )
  }
  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between',
      gap: '8px 20px', padding: '0 16px 0 20px', borderBottom: '1px solid var(--color-border, #dde3ea)',
      background: 'var(--color-bg-secondary, #ffffff)',
    }}>
      {tabs.length === 0 ? (
        // No tabs on this screen: the row still holds the layout switch, so
        // every screen reads the same way.
        <span style={{ minHeight: 48 }} />
      ) : (
      <div role="tablist" aria-label="Tabs"
           style={{ display: 'flex', gap: 22, overflowX: 'auto', minWidth: 0 }}>
        {tabs.map((t) => {
          const on = t.id === active
          return (
            <button key={t.id} type="button" role="tab" aria-selected={on}
                    disabled={busy && !on} onClick={() => { if (!on) onSelect(t.id) }}
                    {...tw('action', `list-tab:${t.id}`)}
                    style={{
                      background: 'none', border: 0, padding: '12px 2px 11px',
                      borderBottom: `2px solid ${on ? (t.color || 'var(--color-accent, #0061D0)') : 'transparent'}`,
                      color: on ? 'var(--color-text, #1c2430)' : 'var(--color-text-secondary, #465062)',
                      fontWeight: on ? 600 : 500, fontSize: '0.875rem',
                      display: 'inline-flex', gap: 8, alignItems: 'center',
                      whiteSpace: 'nowrap', cursor: 'pointer', fontFamily: 'inherit',
                    }}>
              {t.label}
              {showCounts && t.count != null && (
                <span style={{
                  fontSize: '0.8125rem', padding: '0 8px', borderRadius: 999, fontWeight: 500,
                  background: on ? 'var(--color-accent-weak, rgba(0,97,208,0.10))' : 'var(--color-surface-2, #f0f3f7)',
                  color: on ? 'var(--color-accent, #0061D0)' : 'var(--color-text-secondary, #465062)',
                }}>{t.count}</span>
              )}
            </button>
          )
        })}
      </div>
      )}
      {trailing && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0' }}>
          <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary, #465062)' }}>Layout</span>
          {trailing}
        </div>
      )}
    </div>
  )
}
